import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchCashRechargeDetails } from './fetchCashRechargeDetailsSlice';
import { calculateRechargeAmount } from './calculateRechargeAmountSlice';
import { PlantOption, CustomerOption } from '../../interface/CustomerRecharge';

export interface CustomerRechargeUiState {
  selectedRechargeType: string;
  selectedPlanAmount: number;
  selectedPlanName?: string;
  discountCode: string;
  isDiscountApplied: boolean;
  isApplyingDiscount: boolean;
  couponApplySuccess: boolean;
  discountError: string;
  isPayButtonEnabled: boolean;
  selectedPlant: PlantOption | null;
  selectedCustomer: CustomerOption | null;
  formErrors: Record<string, string>;
  discountAmount?: number;
  payAmount?: number;
  displayMessage?: string;
  discountId?: number;
}

const initialState: CustomerRechargeUiState = {
  selectedRechargeType: '',
  selectedPlanAmount: 0,
  selectedPlanName: undefined,
  discountCode: '',
  isDiscountApplied: false,
  isApplyingDiscount: false,
  couponApplySuccess: false,
  discountError: '',
  isPayButtonEnabled: true,
  selectedPlant: null,
  selectedCustomer: null,
  formErrors: {},
  discountAmount: 0,
  payAmount: 0,
  displayMessage: '',
  discountId: undefined,
};

const customerRechargeUiSlice = createSlice({
  name: 'customerRechargeUi',
  initialState,
  reducers: {
    setSelectedPlant(state, action: PayloadAction<PlantOption | null>) {
      state.selectedPlant = action.payload;
    },
    setSelectedCustomer(state, action: PayloadAction<CustomerOption | null>) {
      state.selectedCustomer = action.payload;
    },
    setFormErrors(state, action: PayloadAction<Record<string, string>>) {
      state.formErrors = action.payload;
    },
    setSelectedRechargeType(state, action: PayloadAction<string>) {
      state.selectedRechargeType = action.payload;
    },
    setSelectedPlanAmount(state, action: PayloadAction<number>) {
      state.selectedPlanAmount = action.payload;
    },
    setSelectedPlanName(state, action: PayloadAction<string | undefined>) {
      state.selectedPlanName = action.payload;
    },
    setDiscountCode(state, action: PayloadAction<string>) {
      state.discountCode = action.payload;
    },
    setIsDiscountApplied(state, action: PayloadAction<boolean>) {
      state.isDiscountApplied = action.payload;
    },
    setIsApplyingDiscount(state, action: PayloadAction<boolean>) {
      state.isApplyingDiscount = action.payload;
    },
    setCouponApplySuccess(state, action: PayloadAction<boolean>) {
      state.couponApplySuccess = action.payload;
    },
    setDiscountError(state, action: PayloadAction<string>) {
      state.discountError = action.payload;
    },
    setPayButtonEnabled(state, action: PayloadAction<boolean>) {
      state.isPayButtonEnabled = action.payload;
    },
    resetCustomerRechargeState(state) {
      state.selectedRechargeType = '';
      state.selectedPlanAmount = 0;
      state.selectedPlanName = undefined;
      state.discountCode = '';
      state.isDiscountApplied = false;
      state.isApplyingDiscount = false;
      state.couponApplySuccess = false;
      state.discountError = '';
    },
    reSetCashRechargeDetails(state) {
      // Add any logic needed to reset cashRechargeDetails-related UI state
      state.selectedPlanAmount = 0;
      state.selectedPlanName = undefined;
      state.discountCode = '';
      state.isDiscountApplied = false;
      state.isApplyingDiscount = false;
      state.couponApplySuccess = false;
      state.discountError = '';
    },
    reSetPlant(state) {
      // Add any logic needed to reset plant-related UI state
      state.selectedRechargeType = '';
      state.selectedPlanAmount = 0;
      state.selectedPlanName = undefined;
    },
    resetDiscountState(state) {
      state.discountCode = "";
      state.discountError = "";
      state.isDiscountApplied = false;
      state.couponApplySuccess = false;
    },
    resetAllRechargeState: () => initialState,
  updateCashRechargeDiscountAndPayAmount(state, action: PayloadAction<{ DiscountAmount?: number; PayAmount?: number; DiscountId?: any; RechargeAmount?: number; DisplayMessage?: string; RechargeAmountAfterDiscount?: number }>) {
      // Always log payload for debugging
      // (log removed)
      if (action.payload.DiscountAmount !== undefined) {
        state.discountAmount = action.payload.DiscountAmount;
      }
      // Always set payAmount from RechargeAmountAfterDiscount if present, else PayAmount
      if (action.payload.RechargeAmountAfterDiscount !== undefined) {
        state.payAmount = action.payload.RechargeAmountAfterDiscount;
      } else if (action.payload.PayAmount !== undefined) {
        state.payAmount = action.payload.PayAmount;
      }
      if (action.payload.DisplayMessage !== undefined) {
        state.displayMessage = action.payload.DisplayMessage;
      }
      if (action.payload.DiscountId !== undefined) {
        state.discountId = action.payload.DiscountId;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCashRechargeDetails.fulfilled, (state, action) => {
      // Only use Data.DisplayMessage, never fallback to Status.DisplayMessage
      const dataMsg = action.payload?.Data?.DisplayMessage;
      state.displayMessage = dataMsg ?? "";
    });
    builder.addCase(calculateRechargeAmount.fulfilled, (state, action) => {
      // Only use Data.DisplayMessage, never fallback to Status.DisplayMessage
      const dataMsg = action.payload?.Data?.DisplayMessage;
      state.displayMessage = dataMsg ?? "";
    });
  }
});

export const {
  setSelectedPlant,
  setSelectedCustomer,
  setFormErrors,
  setSelectedRechargeType,
  setSelectedPlanAmount,
  setSelectedPlanName,
  setDiscountCode,
  setIsDiscountApplied,
  setIsApplyingDiscount,
  setCouponApplySuccess,
  setDiscountError,
  setPayButtonEnabled,
  resetCustomerRechargeState,
  reSetCashRechargeDetails,
  reSetPlant,
  resetDiscountState,
  updateCashRechargeDiscountAndPayAmount,
  resetAllRechargeState,
} = customerRechargeUiSlice.actions;
export default customerRechargeUiSlice.reducer;
